import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Upload, UsersRound, FileText, Search, Pencil, QrCode, ShieldCheck, Workflow, MoreVertical } from "lucide-react";
import toast from "react-hot-toast";
import CustomTooltip from "@/components/shared/CustomTooltip";
import ShareJobQrModal from "@/components/shared/ShareJobQrModal";
import JobComplianceChecklistCard from "./components/JobComplianceChecklistCard";
import ChecklistBuilderModal from "@/pages/admin/compliance/checklists/components/ChecklistBuilderModal";
import SendToComplianceModal from "@/pages/admin/candidate/components/SendToComplianceModal";
import ComplianceEmailModal from "@/pages/admin/candidate/components/ComplianceEmailModal";
import { ComplianceService } from "@/services/complianceService";
import { COMPLIANCE_STATUS, getComplianceStatusMeta, indexComplianceRunsByApplication, complianceStatusForRow } from "@/pages/admin/compliance/constant";
import { JobService } from "@/services/jobService";
import { UserService } from "@/services/userService";
import { CandidateService } from "@/services/candidateService";
import { DashboardService } from "@/services/dashboardService";
import CandidateStatusService from "@/services/candidateStatusService";
import useLiveResource from "@/hooks/useLiveResource";
import { formatDate } from "@/utils/dateUtils";
import { getStatusColorIndex } from "@/utils/statusColors";
import { Badge } from "@/components/ui/Badge";
import UndoPendingTag from "@/components/shared/UndoPendingTag";
import { Button } from "@/components/ui/Button";
import { MatchBar } from "@/components/ui/MatchBar";
import Skeleton from "@/components/shared/Skeleton";
import DataTable from "@/components/shared/DataTable";
import PipelineCard from "@/pages/admin/dashboard/components/PipelineCard";
import { EMPLOYEE_TYPE } from "@/config/dropdownData";
import JobDetailsBody from "../job/component/JobDetailsBody";
import CloneJobForm from "../job/CloneJobForm";
import EditJobForm from "../job/EditJobForm";
import RetriggerCandidatesModal from "../job/components/RetriggerCandidatesModal";
import BooleanStringsTab from "./components/BooleanStringsTab";
import JobPriorityBadge from "../job/component/JobPriorityBadge";
import MatchScoreInfo from "../candidate/components/MatchScoreInfo";
import PermissionGuard from "@/permissions/PermissionGuard";
import ModuleGate from "@/permissions/ModuleGate";
import { MODULES } from "@/permissions/modules";
import useModuleAccess from "@/hooks/useModuleAccess";
import { PERMISSIONS } from "@/permissions/constant";
import { hasPermission, getUserPermissions } from "@/permissions/rbac";
import { useAuth } from "@/hooks/useAuth";

const getStatusCode = (status) => {
  if (!status) return "";
  return status.code || status.status || "";
};

const getStatusLabel = (status) => {
  if (!status) return "Unknown";
  return status.name || status.label || status.code || "Unknown";
};

const getPipelineValue = (pipeline, status) => {
  if (!pipeline || !status) return 0;

  if (pipeline.by_status_id && status.id) {
    return pipeline.by_status_id[status.id] ?? 0;
  }

  const code = getStatusCode(status)?.toLowerCase();
  if (pipeline.by_status_code && code) {
    return pipeline.by_status_code[code] ?? 0;
  }

  return pipeline?.[code] ?? 0;
};

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const setHeader = useOutletContext();
  const { user } = useAuth();

  const canViewCandidate = hasPermission(PERMISSIONS.CANDIDATE_VIEW, user.permissions);

  // Entitlement only. Availability is the <ModuleGate> around the button
  // itself: a killed module keeps the column and disables the control, because
  // the candidate rows beside it are not compliance data and must not move.
  // `getUserPermissions` rather than `user.permissions` raw — an unresolved
  // user then yields [] and fails closed instead of throwing.
  const canSendToCompliance = hasPermission(PERMISSIONS.COMPLIANCE_CREATE, getUserPermissions(user));
  const { isEnabled: complianceEnabled } = useModuleAccess(MODULES.COMPLIANCE);

  const [sendCandidate, setSendCandidate] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  // Step 2 of the send — another sibling dialog, never nested (React #299).
  // Holds the checklist step 1 chose; null means it is closed.
  const [sendEmailStep, setSendEmailStep] = useState(null);
  // Who is already in compliance.
  const [complianceStatusByApplication, setComplianceStatusByApplication] = useState({});

  const [job, setJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [pipeline, setPipeline] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  const [activeTab, setActiveTab] = useState(canViewCandidate ? "candidates" : "details");
  const [statusFilterId, setStatusFilterId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Clone. This is the page a finished job is read on, so it is the page the
  // Clone control belongs on — the alternative was going back to the list to
  // find the row again.
  const [cloneOpen, setCloneOpen] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [shareQrOpen, setShareQrOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [actionsMenuStyle, setActionsMenuStyle] = useState({});
  const actionsTriggerRef = useRef(null);
  const actionsMenuRef = useRef(null);
  // The checklist the compliance card opens, read-only. Held HERE rather than
  // inside the card so the builder is a SIBLING of the page's other dialogs
  // and never nests inside one — a dialog mounted inside another's content
  // creates a stacking context it can never paint above (React error #299).
  const [viewChecklistId, setViewChecklistId] = useState(null);

  // Edit, rendered in place rather than routed through the job list — a full
  // navigation to /admin/jobs would flash the list page behind the modal.
  const [editOpen, setEditOpen] = useState(false);
  const [retriggerPicker, setRetriggerPicker] = useState(null);

  // Portaled to `document.body` with `position: fixed`, computed from the
  // trigger's own bounding rect — same approach as JobCard.jsx's actions
  // menu. The header card wraps everything in `overflow-hidden` (for its
  // rounded corners), which clips a plain `absolute` dropdown the moment it
  // is taller than the sliver of card below the trigger.
  const calculateActionsMenuPosition = useCallback(() => {
    if (!actionsTriggerRef.current) return;
    const rect = actionsTriggerRef.current.getBoundingClientRect();
    const MENU_WIDTH = 224;
    const GAP = 4;
    let left = rect.right - MENU_WIDTH;
    if (left < 8) left = 8;
    setActionsMenuStyle({
      position: "fixed",
      top: rect.bottom + GAP,
      left,
      width: MENU_WIDTH,
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    if (actionsOpen) calculateActionsMenuPosition();
  }, [actionsOpen, calculateActionsMenuPosition]);

  useEffect(() => {
    if (!actionsOpen) return undefined;
    window.addEventListener("resize", calculateActionsMenuPosition);
    return () => window.removeEventListener("resize", calculateActionsMenuPosition);
  }, [actionsOpen, calculateActionsMenuPosition]);

  // Closes the actions menu on an outside click or Escape — same convention
  // as ChecklistCardActionsMenu. Both refs are checked because the menu is
  // portaled and no longer lives inside the trigger's DOM subtree.
  useEffect(() => {
    if (!actionsOpen) return undefined;
    const onPointerDown = (e) => {
      if (!actionsTriggerRef.current?.contains(e.target) && !actionsMenuRef.current?.contains(e.target)) {
        setActionsOpen(false);
      }
    };
    const onKeyDown = (e) => e.key === "Escape" && setActionsOpen(false);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [actionsOpen]);

  // Scrolling the page while open would leave the fixed-position menu
  // visually detached from its trigger — closing it matches JobCard.jsx.
  useEffect(() => {
    if (!actionsOpen) return undefined;
    const onScroll = (e) => {
      if (!actionsMenuRef.current?.contains(e.target)) setActionsOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [actionsOpen]);

  // Entitlement only. A killed module keeps its tab, because the strings the
  // tenant already generated must stay readable.
  const { isEnabled: booleanSearchEnabled } = useModuleAccess(MODULES.AI_BOOLEAN_SEARCH);

  // The Boolean Strings tab is dropped from the array, not hidden with CSS.
  // A tab the tenant never bought must not be reachable, and its body must
  // never mount — mounting is what fires the fetch that answers 403.
  // Compliance checklist sits beside Job Description because it answers the
  // same kind of question — what this job is — rather than who applied to it.
  // `complianceEnabled` is entitlement (`isEnabled`), matching the Boolean tab:
  // a tenant who never bought Compliance gets no tab and mounts nothing, while
  // a temporarily-killed module keeps the tab, because the checklist a tenant
  // already wrote must stay readable.
  const TABS = [
    ...(canViewCandidate ? [{ id: "candidates", label: "Candidates", icon: UsersRound }] : []),
    { id: "details", label: "Job Description", icon: FileText },
    ...(complianceEnabled ? [{ id: "compliance", label: "Compliance Checklist", icon: ShieldCheck }] : []),
    ...(booleanSearchEnabled ? [{ id: "boolean", label: "Boolean Strings", icon: Search }] : []),
  ];

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    try {
      const data = await JobService.fetchJobById(jobId);
      setJob(data?.data ?? null);
      setAccessDenied(false);
    } catch (err) {
      console.error("Failed to fetch job detail:", err);
      if (err?.response?.status === 403 && err?.response?.data?.error_code === "INSUFFICIENT_PERMISSIONS") {
        setAccessDenied(true);
      }
    } finally {
      setJobLoading(false);
    }
  }, [jobId]);

  const fetchStats = useCallback(async () => {
    if (!jobId) return;
    try {
      const res = await DashboardService.fetDashboardStats(jobId);
      setPipeline(res?.data?.pipeline_breakdown ?? null);
    } catch (err) {
      console.error("Failed to fetch pipeline stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, [jobId]);

  // Scoped to this job's own flow — otherwise a job on a 3-step flow would
  // show every other flow's steps mixed into its pipeline column list.
  const fetchPipelineStages = useCallback(async (flowId) => {
    if (!flowId) return;
    try {
      setStagesLoading(true);
      const res = await CandidateStatusService.list({
        flowId,
        page: 1,
        per_page: 100,
        sort_by: "sequence",
        sort_order: "asc",
      });
      setPipelineStages(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch candidate statuses:", err);
      setPipelineStages([]);
    } finally {
      setStagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJob();
    fetchStats();
  }, [fetchJob, fetchStats]);

  useEffect(() => {
    fetchPipelineStages(job?.status_flow_id);
  }, [job?.status_flow_id, fetchPipelineStages]);

  useEffect(() => {
    setHeader({
      title: job?.title ?? "Job Detail",
      subTitle: jobLoading ? "Loading…" : `${total} candidate${total !== 1 ? "s" : ""}`,
    });
  }, [setHeader, job, jobLoading, total]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilterId, pageSize]);

  const refreshList = useCallback(() => {
    fetchStats();
    setRefreshKey((k) => k + 1);
  }, [fetchStats]);

  // This job. `job:<id>` names the record, so no narrowing is needed.
  useLiveResource({
    keys: `job:${jobId}`,
    enabled: Boolean(jobId),
    onChange: fetchJob,
  });

  // The candidates on it. None of these events carries a `job:<id>`
  // subscription key — `application.status_changed` declares
  // `('application', 'application:{id}')` — so the page subscribes to each
  // resource namespace and narrows on `parent.job` here.
  //
  // An event whose parent names no job is kept: `candidate.resume_updated`
  // carries `{application}` only, and this list shows a resume-updated badge
  // whichever job the candidate was edited from.
  useLiveResource({
    keys: ["application", "match", "candidate", "bulk_batch"],
    enabled: Boolean(jobId),
    match: (event) => !event.parent?.job || String(event.parent.job) === String(jobId),
    onChange: refreshList,
  });

  // Fetched on the first Clone, not on page load: the list exists only to fill
  // the clone form's "Assign to" select, and most visits never open it.
  const handleOpenClone = useCallback(async () => {
    setCloneOpen(true);
    if (assignableUsers.length) return;
    try {
      const res = await UserService.fetchUsersList({ pagination: false, isActive: true, isDeleted: false });
      const list = res?.data?.users ?? [];
      setAssignableUsers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to fetch users for assignedTo:", err);
    }
  }, [assignableUsers.length]);

  const userOptions = useMemo(
    () =>
      assignableUsers.map((u) => {
        const id = u?.id ?? u?._id;
        return {
          label: user?.id === id ? "Me" : u?.name || String(id ?? ""),
          value: id ? String(id) : "",
        };
      }),
    [assignableUsers, user?.id],
  );

  const isOpen = job?.status?.toLowerCase() === "open";
  const employeeType = EMPLOYEE_TYPE.find((t) => t.value === job?.employee_type)?.label ?? job?.employee_type;

  const activeStage = useMemo(() => pipelineStages.find((stage) => stage.id === statusFilterId), [pipelineStages, statusFilterId]);

  // Who is already in compliance: one read per job rather than one per row,
  // since the table shows a page of one job's applications and a per-row state
  // call would be N requests for an answer that barely varies. The
  // compliance-trigger read that used to sit beside this went with Phase S2.
  //
  // Gated on entitlement AND permission: /compliance/* sits behind the
  // module's router prefix, so an unentitled tenant 403s — and a role holding
  // the `*` wildcard matches `compliance.create` whatever entitlement
  // filtering removed, so it would keep firing without the module check.
  useEffect(() => {
    if (!canSendToCompliance || !complianceEnabled) return undefined;

    let active = true;
    ComplianceService.getCandidates({ pagination: false })
      .then((res) => {
        if (!active) return;
        const rows = res?.data?.candidates ?? res?.candidates ?? [];
        // Keyed on the owning application (Phase G), the same index the
        // candidate list reads. Keying on the candidate gave every
        // application of one person a single answer — this page shows one
        // job's rows, so the candidate's OTHER job's run would have marked
        // this row "Already in Compliance".
        setComplianceStatusByApplication(indexComplianceRunsByApplication(rows));
      })
      .catch((e) => {
        console.error(e);
        if (active) setComplianceStatusByApplication({});
      });

    return () => {
      active = false;
    };
  }, [canSendToCompliance, complianceEnabled, refreshKey]);

  const COLUMNS = [
    {
      key: "full_name",
      label: "Candidate",
      sortable: true,
      render: (val, row) => (
        <Button
          onClick={(e) => {
            // The row itself is clickable (`onRowClick` below) — without this,
            // one click on the name bubbled up and pushed the details route
            // twice, so Back had to be pressed twice to leave the page.
            e.stopPropagation();
            navigate(`/admin/candidate-analysis/${row.id}?job_id=${row.job_id}`);
          }}
          className="text-left text-text-primary hover:text-text-blue font-medium text-[13px] p-0"
        >
          {row.full_name ?? val}
        </Button>
      ),
    },
    {
      key: "match_score",
      label: (
        <div className="flex items-center gap-1">
          Match Indicator <MatchScoreInfo />
        </div>
      ),
      sortable: true,
      render: (val, row) => <MatchBar score={val != null ? val / 10 : null} status={row.match_status} />,
    },
    {
      key: "total_experience",
      label: "Experience",
      sortable: true,
      align: "center",
      render: (val) => <span className="text-text-primary text-[12px]">{val ?? "—"}</span>,
    },
    {
      key: "notice_period",
      label: "Notice",
      sortable: true,
      align: "center",
      render: (val) => <span className="text-text-primary text-[12px]">{val ?? "—"}</span>,
    },
    {
      key: "candidate_status",
      label: "Status",
      sortable: false,
      render: (_val, row) => {
        const statusObj = row?.candidate_status || row?.status_detail || { code: row?.status, name: row?.status };
        const statusLabel = getStatusLabel(statusObj);
        return (
          // Same shape the candidates list wears: the pill sizes to its
          // label on one line and only ever narrows to the column itself.
          // The old fixed `w-24` wrapped two-word tenant statuses ("Screening
          // Rejected") into a second line that spilled out of the pill.
          <div className="flex flex-col items-center">
            <Badge
              label={statusLabel}
              title={statusLabel}
              badgeStyle="text-[11px]"
              variant="gradient"
              colorIndex={getStatusColorIndex(statusObj, pipelineStages)}
              className="h-6 border min-w-[88px] max-w-full whitespace-nowrap text-center rounded-[2rem]"
            />
            {row?.undo_request_pending && <UndoPendingTag />}
          </div>
        );
      },
    },
    {
      key: "applied_at",
      label: "Applied",
      sortable: true,
      align: "center",
      render: (val) => <span className="text-text-primary text-[12px]">{formatDate(val)}</span>,
    },
    // Built into the array, not rendered-then-hidden: a recruiter without
    // COMPLIANCE_CREATE must see no Actions column at all, header included,
    // rather than an empty one that reads as broken.
    ...(canSendToCompliance
      ? [
          {
            key: "compliance_actions",
            label: "Actions",
            sortable: false,
            align: "center",
            render: (_val, row) => {
              // This row is an application of the job being viewed, so its
              // own application id is what answers "has THIS been sent".
              const complianceStatus = complianceStatusForRow(complianceStatusByApplication, row);
              const alreadySent = Boolean(complianceStatus);
              // The send route refuses a rejected application, so the button does too.
              const isRejected = Boolean(row.status_is_rejected);
              // Phase S2: no stage test. Compliance starts from anywhere in the
              // pipeline; only a live run on this application, or a
              // rejection, closes the action.
              const isActionable = !alreadySent && !isRejected;

              let tooltipText = "Send to Compliance";
              if (alreadySent) {
                const meta = getComplianceStatusMeta(complianceStatus);
                tooltipText = meta?.label ? `Already in Compliance (${meta.label})` : "Already in Compliance";
              } else if (isRejected) {
                tooltipText = "Rejected candidates cannot be sent to compliance";
              }

              return (
                <div className="flex gap-2 justify-center">
                  <ModuleGate module={MODULES.COMPLIANCE}>
                    <CustomTooltip text={tooltipText}>
                      <Button
                        aria-label={tooltipText}
                        onClick={(e) => {
                          // The row navigates on click. Without this, one
                          // click both opens the modal and pushes the details
                          // route, unmounting it.
                          e.stopPropagation();
                          if (!isActionable) return;
                          setSendCandidate(row);
                          setSendOpen(true);
                        }}
                        disabled={!isActionable}
                        className="px-3.5 py-2.5 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-background-icon transition-none"
                      >
                        <ShieldCheck size={16} />
                      </Button>
                    </CustomTooltip>
                  </ModuleGate>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const extraParams = {
    job_id: jobId,
    status_id: statusFilterId || undefined,
    pagination: true,
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (accessDenied) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center h-full pt-16">
        <h2 className="text-xl font-semibold text-text-primary mb-2">Access Denied</h2>
        <p className="text-[13px] text-text-secondary mb-6 max-w-md text-center">
          You don't have permission to access this job. It may be restricted or belong to another team.
        </p>
        <Button
          onClick={() => navigate("/admin/jobs")}
          className="px-5 py-2.5 shadow-sm text-sm bg-button-secondary text-text-base border border-border-primary rounded-lg font-medium"
        >
          Back to Jobs List
        </Button>
      </div>
    );
  }

  return (
    <PermissionGuard permission={PERMISSIONS.JOB_VIEW}>
      <div className="flex flex-col gap-3" style={{ zoom: 0.95 }}>
        <Button
          onClick={() => navigate("/admin/jobs")}
          className="flex w-fit items-center gap-1 rounded-lg border border-border-primary bg-background-tertiary text-text-secondary hover:text-text-primary text-[13px]"
        >
          <ArrowLeft size={14} /> Back to Jobs
        </Button>

        <div className="rounded-xl border border-border-primary bg-background-secondary overflow-hidden">
          <div className="p-4 sm:p-5">
            {jobLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-6 w-56" />
                <Skeleton className="h-4 w-80" />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <h1 className="text-lg sm:text-xl font-semibold text-text-primary leading-tight truncate min-w-0 flex-1">{job?.title ?? "—"}</h1>

                    <span className={`rounded-full p-1 border ${isOpen ? "border-green-500" : "border-red-500"}`}>
                      <span
                        className={`flex items-center justify-center text-[12px] px-2 rounded-full bg-background-secondary w-full h-full ${isOpen ? "text-green-500" : "text-red-500"}`}
                      >
                        <span className="mr-1">•</span>
                        {isOpen ? "Open" : "Closed"}
                      </span>
                    </span>

                    <JobPriorityBadge priority={job?.job_priority} />
                  </div>

                  <div className="relative shrink-0">
                    <Button
                      ref={actionsTriggerRef}
                      onClick={() => setActionsOpen((prev) => !prev)}
                      title="Job actions"
                      aria-label="Job actions"
                      className="flex items-center justify-center text-text-secondary p-1.5 rounded-lg border border-border-primary hover:bg-background-tertiary/30"
                    >
                      <MoreVertical size={16} />
                    </Button>

                    {actionsOpen &&
                      createPortal(
                        <div
                          ref={actionsMenuRef}
                          style={actionsMenuStyle}
                          className="flex flex-col bg-background-form border border-border-primary rounded-xl shadow-xl px-1 py-1 text-sm"
                        >
                        {isOpen && (
                          <PermissionGuard permission={PERMISSIONS.JOB_UPDATE} isComponent>
                            <Button
                              onClick={() => {
                                setActionsOpen(false);
                                setEditOpen(true);
                              }}
                              className="flex items-center gap-3 text-text-primary w-full px-3 py-2.5 !rounded-md hover:bg-background-primary text-left whitespace-nowrap"
                            >
                              <Pencil size={14} />
                              Edit Job
                            </Button>
                          </PermissionGuard>
                        )}

                        {/* No `isOpen` gate. Cloning a closed job is the primary
                            use case — the Edit item's open-only rule must not be
                            copied here. */}
                        <PermissionGuard permission={PERMISSIONS.JOB_CREATE} isComponent>
                          <Button
                            onClick={() => {
                              setActionsOpen(false);
                              handleOpenClone();
                            }}
                            className="flex items-center gap-3 text-text-primary w-full px-3 py-2.5 !rounded-md hover:bg-background-primary text-left whitespace-nowrap"
                          >
                            <Copy size={14} />
                            Clone Job
                          </Button>
                        </PermissionGuard>

                        <PermissionGuard permission={PERMISSIONS.JOB_VIEW} isComponent>
                          {isOpen ? (
                            <Button
                              onClick={() => {
                                setActionsOpen(false);
                                setShareQrOpen(true);
                              }}
                              className="flex items-center gap-3 text-text-primary w-full px-3 py-2.5 !rounded-md hover:bg-background-primary text-left whitespace-nowrap"
                            >
                              <QrCode size={14} />
                              Share QR Code
                            </Button>
                          ) : (
                            <CustomTooltip text="This job is closed and cannot be shared" className="w-full">
                              <Button
                                disabled
                                className="flex items-center gap-3 text-text-primary w-full px-3 py-2.5 !rounded-md text-left opacity-50 cursor-not-allowed whitespace-nowrap"
                              >
                                <QrCode size={14} />
                                Share QR Code
                              </Button>
                            </CustomTooltip>
                          )}
                        </PermissionGuard>

                        {isOpen && (
                          <PermissionGuard permissions={[PERMISSIONS.CANDIDATE_CREATE, PERMISSIONS.CANDIDATE_UPDATE]} isComponent>
                            <ModuleGate module={MODULES.BULK_INTAKE} className="w-full">
                              <Button
                                onClick={() => {
                                  setActionsOpen(false);
                                  navigate(`/admin/job-detail/${jobId}/bulk-upload`);
                                }}
                                className="flex items-center gap-3 text-text-primary w-full px-3 py-2.5 !rounded-md hover:bg-background-primary text-left whitespace-nowrap"
                              >
                                <Upload size={14} />
                                Bulk Upload Resumes
                              </Button>
                            </ModuleGate>
                          </PermissionGuard>
                        )}
                        </div>,
                        document.body,
                      )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-border-primary" />

          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {/* The stages below are this job's flow, and a tenant can have
                    several — the name says which one the recruiter is reading. */}
                {job?.status_flow_name && (
                  <span className="flex items-center gap-1 text-xs text-text-secondary">
                    <Workflow size={12} aria-hidden="true" />
                    Status Flow: <span className="font-medium text-text-primary">{job.status_flow_name}</span>
                  </span>
                )}
                <PermissionGuard permission={PERMISSIONS.CANDIDATE_VIEW} isComponent>
                  <p className="text-xs text-text-secondary">Click any pipeline stage to filter candidates by status.</p>
                </PermissionGuard>
              </div>

              {statusFilterId && (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-text-primary bg-background-icon/30 border border-border-primary rounded-full px-2 py-1">
                    Filter: {activeStage ? getStatusLabel(activeStage) : "Selected"}
                  </span>

                  <Button onClick={() => setStatusFilterId("")} className="text-xs text-text-blue hover:underline">
                    Clear Filter
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
              {stagesLoading
                ? Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="rounded-lg border border-border-primary p-3">
                      <Skeleton className="h-12 w-full" />
                    </div>
                  ))
                : pipelineStages.map((stage, i) => {
                    const active = statusFilterId === stage.id;

                    return (
                      <Button
                        key={stage.id}
                        onClick={() => {
                          if (canViewCandidate) {
                            setStatusFilterId((prev) => (prev === stage.id ? "" : stage.id));
                            setActiveTab("candidates");
                          }
                        }}
                        aria-pressed={active}
                        className={`rounded-lg px-2 text-left transition-all ${
                          active ? "ring-2 ring-text-blue ring-offset-2 ring-offset-background-secondary" : "hover:opacity-80"
                        }`}
                      >
                        <PipelineCard label={getStatusLabel(stage)} value={getPipelineValue(pipeline, stage)} colorIdx={i} loading={statsLoading} />
                      </Button>
                    );
                  })}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border-primary bg-background-secondary overflow-hidden">
          <div className="flex items-center gap-0 border-b border-border-primary px-4">
            {TABS.map((tab) => {
              const active = activeTab === tab.id;
              return (
                <Button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex items-center gap-1.5 px-3 py-3 text-[13px] font-medium transition-colors ${
                    active ? "text-text-primary" : "text-text-secondary hover:text-text-primary"
                  }`}
                >
                  <tab.icon size={14} />
                  {tab.label}
                  {active && <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-text-blue rounded-t-full" />}
                  {tab.id === "candidates" && !listLoading && total > 0 && (
                    <span className="ml-1 text-[10px] px-2 py-0.5 rounded-full bg-background-icon/40 border border-border-primary text-text-secondary tabular-nums">
                      {activeStage ? getPipelineValue(pipeline, activeStage) : total}
                    </span>
                  )}
                </Button>
              );
            })}
          </div>

          {activeTab === "candidates" ? (
            <DataTable
              key={`${jobId}-${statusFilterId}-${currentPage}-${pageSize}-${refreshKey}`}
              rowKey="application_id"
              api={CandidateService.fetCandidateList}
              columns={COLUMNS}
              extraParams={extraParams}
              setTotal={setTotal}
              setLoading={setListLoading}
              onRowClick={(row) => navigate(`/admin/candidate-analysis/${row.id}?job_id=${row.job_id}`)}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              showPagination
              paginationLabel="candidates"
            />
          ) : activeTab === "compliance" ? (
            <div className="p-4 sm:p-6">
              {/* Which checklist this job's candidates get by default when they
                  are sent to compliance (Phase D). ModuleGate lives inside the
                  component, so an unentitled tenant never mounts it and
                  therefore never fires the read. */}
              <JobComplianceChecklistCard jobId={jobId} />
            </div>
          ) : activeTab === "boolean" ? (
            <div className="p-4 sm:p-6">
              {/* `passthrough`: the tab owns its own unavailable state — one
                  banner, Generate disabled, every generated string still
                  expandable and copyable. The default disabled wrapper set
                  `pointer-events-none` over the whole panel, which locked the
                  accordion shut and made the strings unreadable, and its
                  paragraph repeated the tab's own banner. */}
              <ModuleGate module={MODULES.AI_BOOLEAN_SEARCH} variant="passthrough">
                <BooleanStringsTab jobId={jobId} />
              </ModuleGate>
            </div>
          ) : (
            <div className="p-4 sm:p-6">
              <JobDetailsBody viewJob={job} viewLoading={jobLoading} />
            </div>
          )}
        </div>

        {/* The clone lands on its own detail page, so the recruiter carries on
            in the new job rather than hunting for it in the list. */}
        <CloneJobForm
          open={cloneOpen}
          jobId={jobId}
          onClose={() => setCloneOpen(false)}
          onSuccess={(newJobId) => {
            if (newJobId) navigate(`/admin/job-detail/${newJobId}`);
          }}
          userOptions={userOptions}
        />
        <EditJobForm
          open={editOpen}
          jobId={jobId}
          onClose={() => setEditOpen(false)}
          onSuccess={({ changedScoreFields } = {}) => {
            setEditOpen(false);
            toast.success("Job updated successfully.", { position: "top-right" });
            fetchJob();
            if (changedScoreFields?.length) {
              setRetriggerPicker({ jobId, changedFields: changedScoreFields });
            }
          }}
          userOptions={userOptions}
        />
        <RetriggerCandidatesModal
          open={!!retriggerPicker}
          jobId={retriggerPicker?.jobId}
          changedFields={retriggerPicker?.changedFields || []}
          onClose={() => setRetriggerPicker(null)}
        />
        <ShareJobQrModal open={shareQrOpen} onOpenChange={setShareQrOpen} jobId={jobId} jobTitle={job?.title} />

        {/* `readOnly` turns the builder into the Checklist Details screen, so
            the view and the editor can never drift apart — there is no second
            view modal to keep in step. */}
        {/* A sibling of the page's other dialogs, mounted once — never inside
            a table cell, which would mount one dialog per row. The eye inside
            it reuses `viewChecklistId`, and `open` withdraws this modal while
            the builder is up so the two never overlap (React error #299). */}
        <SendToComplianceModal
          open={sendOpen && !viewChecklistId && !sendEmailStep}
          candidate={sendCandidate}
          jobId={sendCandidate?.job_id ?? jobId}
          jobTitle={sendCandidate?.job_title ?? job?.title}
          applicationId={sendCandidate?.application_id}
          onViewChecklist={setViewChecklistId}
          onNext={({ checklistId }) => setSendEmailStep({ checklistId })}
          onClose={() => {
            setSendOpen(false);
            setSendCandidate(null);
          }}
          onSent={() => {
            // The table does not subscribe to compliance events, so the row
            // would keep offering Send until the next refetch. Record it
            // locally, the way the candidate list does — keyed on the
            // application that was just sent, matching the index this map is
            // read through (Phase G).
            const applicationId = sendCandidate?.application_id;
            if (!applicationId) return;
            setComplianceStatusByApplication((prev) => ({ ...prev, [String(applicationId)]: COMPLIANCE_STATUS.PENDING_FROM_CANDIDATE }));
          }}
        />

        <ComplianceEmailModal
          open={!!sendEmailStep && !viewChecklistId}
          candidate={sendCandidate}
          applicationId={sendCandidate?.application_id}
          checklistId={sendEmailStep?.checklistId}
          onBack={() => setSendEmailStep(null)}
          onClose={() => {
            setSendEmailStep(null);
            setSendOpen(false);
            setSendCandidate(null);
          }}
          onSent={() => {
            // The table does not subscribe to compliance events, so record the
            // send locally — keyed on the application, the same rule step 1's
            // onSent keeps (Phase G).
            const applicationId = sendCandidate?.application_id;
            if (!applicationId) return;
            setComplianceStatusByApplication((prev) => ({ ...prev, [String(applicationId)]: COMPLIANCE_STATUS.PENDING_FROM_CANDIDATE }));
          }}
        />

        {viewChecklistId && <ChecklistBuilderModal open readOnly checklistId={viewChecklistId} onClose={() => setViewChecklistId(null)} />}
      </div>
    </PermissionGuard>
  );
};

export default JobDetail;
